import React, { useEffect, useMemo, useState } from "react";
import { api } from "../lib/api";
import { Link } from "react-router-dom";
import { Plus, Trash2, ClipboardCheck, CircleCheck, CircleDot, ArrowRight } from "lucide-react";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { fmtDate, fmtDateTime } from "../lib/format";
import { useConfirm } from "../components/ConfirmDialog";
import { toast } from "sonner";

export default function Inventories() {
  const [items, setItems] = useState([]);
  const [distributors, setDistributors] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ distributor_id: "", notes: "", mode: "BY_PRODUCT" });
  const confirm = useConfirm();

  const load = async () => {
    const [i, d] = await Promise.all([api.get("/inventories"), api.get("/distributors")]);
    setItems(i.data); setDistributors(d.data);
  };
  useEffect(() => { load(); }, []);

  const distMap = useMemo(() => Object.fromEntries(distributors.map((d) => [d.id, d])), [distributors]);

  const save = async () => {
    if (!form.distributor_id) { toast.error("Sélectionnez un dépôt"); return; }
    try {
      const { data } = await api.post("/inventories", form);
      if (data.items.length === 0) {
        toast.warning("Aucun lot avec stock dans ce dépôt — inventaire créé mais vide");
      } else {
        toast.success(`Inventaire créé (${data.items.length} lots à compter)`);
      }
      setOpen(false);
      setForm({ distributor_id: "", notes: "", mode: "BY_PRODUCT" });
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const remove = async (inv) => {
    const ok = await confirm({
      title: `Supprimer l'inventaire ${inv.reference} ?`,
      destructive: true,
      confirmText: "Supprimer",
    });
    if (!ok) return;
    await api.delete(`/inventories/${inv.id}`);
    toast.success("Inventaire supprimé");
    await load();
  };

  const countDiscrepancies = (inv) => {
    let counted = 0, discrepancies = 0;
    for (const it of inv.items) {
      if (it.counted_quantity !== null && it.counted_quantity !== undefined) {
        counted++;
        if (it.counted_quantity !== it.theoretical_quantity) discrepancies++;
      }
    }
    return { counted, discrepancies, total: inv.items.length };
  };

  return (
    <div className="space-y-6" data-testid="inventories-page">
      <div className="flex items-center justify-between gap-3">
        <div className="text-sm text-[#575A57]">
          <ClipboardCheck className="w-4 h-4 inline mr-2 -mt-0.5" />
          {items.length} inventaire{items.length > 1 ? "s" : ""} — théorique vs physique par dépôt
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="create-inventory-btn">
              <Plus className="w-4 h-4 mr-2" /> Démarrer un inventaire
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle className="font-heading">Nouvel inventaire</DialogTitle></DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="mb-1.5 block">Dépôt à inventorier *</Label>
                <Select value={form.distributor_id} onValueChange={(v) => setForm({ ...form, distributor_id: v })}>
                  <SelectTrigger data-testid="inv-depot"><SelectValue placeholder="Sélectionner…" /></SelectTrigger>
                  <SelectContent>
                    {distributors.map((d) => <SelectItem key={d.id} value={d.id}>{d.name}{d.code ? ` (${d.code})` : ""}</SelectItem>)}
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#575A57] mt-1.5">Un snapshot des lots présents dans ce dépôt sera créé.</p>
              </div>

              {/* Phase 91 — Mode de comptage. BY_PRODUCT = 1 ligne par produit (recommandé centrale). */}
              <div>
                <Label className="mb-1.5 block">Mode de comptage</Label>
                <Select value={form.mode} onValueChange={(v) => setForm({ ...form, mode: v })}>
                  <SelectTrigger data-testid="inv-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BY_PRODUCT" data-testid="inv-mode-by-product">⚡ Rapide — par produit (recommandé)</SelectItem>
                    <SelectItem value="BY_LOT" data-testid="inv-mode-by-lot">🔬 Détaillé — par lot HACCP</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#575A57] mt-1.5">
                  {form.mode === "BY_PRODUCT"
                    ? "1 ligne par produit (somme de tous les lots). Vous comptez le total physique, le système réconcilie les lots en FIFO."
                    : "1 ligne par lot. Pour audit HACCP strict — chaque lot compté individuellement avec sa DLC."}
                </p>
              </div>

              <div>
                <Label className="mb-1.5 block">Notes</Label>
                <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} placeholder="Ex: Inventaire mensuel février 2026" />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Annuler</Button>
              <Button onClick={save} className="bg-[#1F4E3D] hover:bg-[#173B2E]" data-testid="save-inventory-btn">Démarrer</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <div className="bg-white border border-[#E5E5E0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#F4F4F0] hover:bg-[#F4F4F0]">
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Référence</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Dépôt</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Statut</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Progression</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Écarts</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Démarré</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57]">Clôturé</TableHead>
              <TableHead className="text-xs uppercase tracking-wider font-semibold text-[#575A57] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody data-testid="inventories-table-body">
            {items.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-[#575A57] py-12">
                  <ClipboardCheck className="w-8 h-8 mx-auto mb-2 opacity-40" />
                  Aucun inventaire. Démarrez-en un pour réconcilier le stock théorique et physique.
                </TableCell>
              </TableRow>
            ) : items.map((inv) => {
              const { counted, discrepancies, total } = countDiscrepancies(inv);
              const dist = distMap[inv.distributor_id];
              return (
                <TableRow key={inv.id} data-testid={`inventory-row-${inv.reference}`}>
                  <TableCell className="font-mono font-medium">{inv.reference}</TableCell>
                  <TableCell>{dist?.name || "—"}{dist?.code && <span className="text-xs font-mono text-[#575A57] ml-1">({dist.code})</span>}</TableCell>
                  <TableCell>
                    {inv.status === "OPEN" ? (
                      <Badge variant="outline" className="bg-[#FBF4E9] border-[#E8D3A8] text-[#8C6B2F] gap-1.5">
                        <CircleDot className="w-3 h-3" /> En cours
                      </Badge>
                    ) : (
                      <Badge variant="outline" className="bg-[#EBF2E9] border-[#C7DDC0] text-[#2D6A4F] gap-1.5">
                        <CircleCheck className="w-3 h-3" /> Clôturé
                      </Badge>
                    )}
                  </TableCell>
                  <TableCell className="text-sm font-mono">{counted} / {total}</TableCell>
                  <TableCell className="text-right font-mono">
                    {discrepancies > 0 ? (
                      <span className="text-[#C05C47] font-semibold">{discrepancies}</span>
                    ) : counted > 0 ? (
                      <span className="text-[#2D6A4F]">0</span>
                    ) : (
                      <span className="text-[#A3A6A3]">—</span>
                    )}
                  </TableCell>
                  <TableCell className="text-sm">{fmtDateTime(inv.created_at)}</TableCell>
                  <TableCell className="text-sm">{fmtDateTime(inv.closed_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Link to={`/inventaires/${inv.id}`}>
                        <Button variant="ghost" size="sm" data-testid={`open-inventory-${inv.reference}`}>
                          Ouvrir <ArrowRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                      <Button variant="ghost" size="icon" onClick={() => remove(inv)}>
                        <Trash2 className="w-4 h-4 text-[#C05C47]" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
